import {
  formatDateDB,
  toMoment,
} from '@fortress-technology-solutions/fortress-component-library/utils';
import { isRecertComplete, isSignedCopyChecked } from './completion';

// Converts between the form values (flat, yes/no strings) and the legacy RecertForm payload.

const BOOLEAN_FIELDS = [
  'isStudent',
  'TANFAssistance',
  'jobTrainingProgram',
  'singleParentDependantChild',
  'marriedJointReturn',
  'fosterProgram',
];

const COUNT_FIELDS = [
  'anticipateChangeHouseholdPregnancy',
  'anticipateChangeHouseholdAdoption',
  'anticipateChangeHouseholdFoster',
];

const EMPLOYERS = [
  { prefix: 'currentJob', key: 'applicantsCurrentEmployer', hasEndDate: false },
  {
    prefix: 'secondJob',
    key: 'applicantsSecondCurrentEmployer',
    hasEndDate: false,
  },
  {
    prefix: 'previousJob',
    key: 'applicantsPreviousEmployer',
    hasEndDate: true,
  },
];

const isBlank = (value) =>
  value === undefined || value === null || value === '';

const toYesNo = (value) => {
  if (value === true) return 'yes';
  if (value === false) return 'no';
  return undefined;
};

const fromYesNo = (value) => {
  if (value === 'yes') return true;
  if (value === 'no') return false;
  return null;
};

// The checkbox takes a boolean; the library can also hand back 'on' / '' for a checked / unchecked one.
const toCheckbox = (value) => (typeof value === 'boolean' ? value : undefined);

const toText = (value) => (isBlank(value) ? undefined : String(value));
const orNull = (value) => (isBlank(value) ? null : value);
const toNumber = (value) => (isBlank(value) ? null : Number(value));
const toDateValue = (value) => (isBlank(value) ? undefined : toMoment(value));
const toDate = (value) => (isBlank(value) ? null : formatDateDB(value, false));
const toDigits = (value) => {
  const digits = String(value ?? '').replace(/\D/g, '');
  return digits === '' ? null : digits;
};

const employerToFormValues = ({ prefix, hasEndDate }, employer) => {
  const job = employer ?? {};
  return {
    [`${prefix}Position`]: job.position ?? undefined,
    [`${prefix}AnnualSalary`]: job.annualSalary ?? undefined,
    [`${prefix}StartDate`]: toDateValue(job.from),
    ...(hasEndDate && { [`${prefix}EndDate`]: toDateValue(job.to) }),
    [`${prefix}EmployerName`]: job.employerName ?? undefined,
    [`${prefix}EmployerPhone`]: job.employerPhone ?? undefined,
    [`${prefix}SupervisorName`]: job.supervisorName ?? undefined,
    [`${prefix}StreetAddress`]: job.employerStreetAddress ?? undefined,
    [`${prefix}City`]: job.city ?? undefined,
    [`${prefix}State`]: job.state ?? undefined,
    [`${prefix}ZipCode`]: job.zipCode ?? undefined,
  };
};

/** The legacy GET response (or nothing, for a recert never saved) as form values. */
export const toFormValues = (data) => {
  const saved = data ?? {};
  const values = {
    anticipateChangeHousehold: toYesNo(saved.anticipateChangeHousehold),
    studentType: saved.studentType ?? undefined,
    otherStudentStatus: saved.otherStudentStatus ?? undefined,
    employmentStatus: saved.employmentStatusId ?? undefined,
    anticipateChangeEmployer: toYesNo(saved.anticipateChangeEmployer),
    hasSignedCopy: toCheckbox(saved.hasSignedCopy),
  };
  COUNT_FIELDS.forEach((field) => {
    values[field] = toText(saved[field]);
  });
  BOOLEAN_FIELDS.forEach((field) => {
    values[field] = toYesNo(saved[field]);
  });
  return EMPLOYERS.reduce(
    (all, employer) => ({
      ...all,
      ...employerToFormValues(employer, saved[employer.key]),
    }),
    values,
  );
};

const employerToPayload = ({ prefix, hasEndDate }, values, wasSaved) => {
  const job = {
    position: orNull(values[`${prefix}Position`]),
    annualSalary: toNumber(values[`${prefix}AnnualSalary`]),
    from: toDate(values[`${prefix}StartDate`]),
    to: hasEndDate ? toDate(values[`${prefix}EndDate`]) : null,
    employerName: orNull(values[`${prefix}EmployerName`]),
    employerPhone: toDigits(values[`${prefix}EmployerPhone`]),
    supervisorName: orNull(values[`${prefix}SupervisorName`]),
    employerStreetAddress: orNull(values[`${prefix}StreetAddress`]),
    city: orNull(values[`${prefix}City`]),
    state: orNull(values[`${prefix}State`]),
    zipCode: orNull(values[`${prefix}ZipCode`]),
  };
  const isEmpty = Object.values(job).every((value) => value === null);
  // An emptied employer that was saved before is sent as nulls so it is cleared; one never saved
  // is left out so no empty job row is created.
  return isEmpty && !wasSaved ? null : job;
};

/**
 * The form values as the legacy PUT payload. Never carries the signature image: completion only
 * needs the required answers and the signed-copy box.
 */
export const toPayload = (values, { isPrimary, loaded, employedStatusId }) => {
  const studentInformation = BOOLEAN_FIELDS.reduce(
    (info, field) => ({ ...info, [field]: fromYesNo(values[field]) }),
    {
      studentType: orNull(values.studentType),
      otherStudentStatus: orNull(values.otherStudentStatus),
    },
  );
  // The API refuses a null for the yes/no answer, so an unanswered question is left out.
  const householdAnswer = fromYesNo(values.anticipateChangeHousehold);
  const changeHouseholdInformation = isPrimary
    ? COUNT_FIELDS.reduce(
        (info, field) => ({ ...info, [field]: toNumber(values[field]) }),
        householdAnswer === null
          ? {}
          : { anticipateChangeHousehold: householdAnswer },
      )
    : null;

  return {
    changeHouseholdInformation,
    studentInformation,
    employmentStatusId: orNull(values.employmentStatus),
    anticipateChangeEmployer: fromYesNo(values.anticipateChangeEmployer),
    // Always sent, so unchecking the box or clearing an answer makes the saved recert incomplete.
    isComplete: isRecertComplete(values, { employedStatusId }),
    // Left out when never touched, so an untouched form does not write a value.
    ...(values.hasSignedCopy === undefined || values.hasSignedCopy === null
      ? {}
      : { hasSignedCopy: isSignedCopyChecked(values.hasSignedCopy) }),
    ...EMPLOYERS.reduce(
      (employers, employer) => ({
        ...employers,
        [employer.key]: employerToPayload(
          employer,
          values,
          Boolean(loaded?.[employer.key]),
        ),
      }),
      {},
    ),
  };
};
